import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { BaseModal } from 'components/UI';
import moment from 'moment';
import Done from '@material-ui/icons/Done';
import { ListItem, ListItemText } from '@material-ui/core';
import { useStyles, listItemsStyles, listTextStyles } from './BrokerInfoModal.styled';
import BrokerSettingsForm from './BrokerSettingsForm';
import { isEqual } from 'lodash';

import { vehicleActions } from 'state/modules/vehicles';
import { FETCH_STATE } from 'constants';

const BrokerInfoModal = ({ vehicle_id, type, open, broker, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();

  const classes = useStyles();
  const listClasses = listTextStyles();
  const listItemsClasses = listItemsStyles();

  const { brokerSettings, fetchedBrokerSettingsSuccess, updatedBrokerSettingsSucces } = useSelector(
    (state) => state.vehicles
  );

  const loading =
    fetchedBrokerSettingsSuccess === FETCH_STATE.LOADING ||
    updatedBrokerSettingsSucces === FETCH_STATE.LOADING;

  useEffect(() => {
    if (vehicle_id && broker && type === 'vehicle') {
      dispatch(vehicleActions.fetchGetBrokerSettings(vehicle_id, broker.broker_slug));
    }
  }, []);

  useEffect(() => {
    if (updatedBrokerSettingsSucces === FETCH_STATE.DONE) {
      if (handleClick) {
        handleClick();
      }
      dispatch(vehicleActions.resetUpdateBrokerSettings());
    }
  }, [updatedBrokerSettingsSucces]);

  const BrokerStatus = ({ status, details }) => {
    return (
      <span>
        <Done className={classes.status} />
        {status ? (
          <>
            <span>
              {i18n.t(`status.${status}`, {
                defaultValue: status?.toLowerCase() || '--',
              })}
            </span>
            <span>{details?.toString()?.toLowerCase()?.replace(',', ', ')}</span>
          </>
        ) : (
          ''
        )}
      </span>
    );
  };

  const getBrokerDetails = () => {
    const brokerInfo = [
      {
        label: i18n.t('brokers.broker_name'),
        value: (
          <span style={{ textTransform: 'capitalize' }}>
            {i18n.t(`brokers_slugs.${broker?.name || broker?.broker_slug}`, {
              defaultValue: broker.name || broker?.broker_slug,
            }) || '--'}
          </span>
        ),
      },
      {
        label: i18n.t('brokers.status'),
        value: <BrokerStatus status={broker?.status} details={broker?.status_details} />,
      },
      {
        label: i18n.t('brokers.created_time'),
        value: broker?.created_time ? moment(broker?.created_time).format('D MMM, YYYY') : '--',
      },
      { label: i18n.t('brokers.id'), value: broker?.ext_id },
    ];
    return brokerInfo;
  };

  const handleOnEnter = () => {
    if (formRef?.current) {
      formRef.current.submitForm();
    } else if (handleClick) {
      handleClick();
    }
  };

  const handleSubmit = (values) => {
    if (!brokerSettings?.payload || isEqual(values, brokerSettings.payload)) {
      if (handleClick) {
        handleClick();
      }
    } else {
      dispatch(
        vehicleActions.fetchUpdateBrokerSettings(vehicle_id, broker.broker_slug, {
          payload: values,
        })
      );
    }
  };

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      onEnter={handleOnEnter}
      enableCloseButton={false}
      title={i18n.t('brokers.information')}
      enterButtonLabel={i18n.t('common.accept')}
      loading={loading}
    >
      {getBrokerDetails().map((detail, index) => (
        <ListItem key={index} classes={listItemsClasses}>
          <ListItemText classes={listClasses} primary={detail.label} secondary={detail.value} />
        </ListItem>
      ))}
      <BrokerSettingsForm
        formRef={formRef}
        brokerSettings={brokerSettings}
        listClasses={listClasses}
        listItemsClasses={listItemsClasses}
        onSubmit={handleSubmit}
      />
    </BaseModal>
  );
};

BrokerInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  broker: PropTypes.any.isRequired,
  handleClick: PropTypes.func.isRequired,
  vehicle_id: PropTypes.number,
  type: PropTypes.string,
  status: PropTypes.string,
  details: PropTypes.string,
};

BrokerInfoModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default BrokerInfoModal;
